import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { IconAlertTriangle, IconUpload, IconVideo, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

//constants -> esto enrealidad deberia o venir de una config o de be, son constrains de mercadolibre para los clips
const MAX_SIZE = 100 * 1024 * 1024; // 100 MB
const MIN_HEIGHT = 640;
const MIN_WIDTH = 360;
const MIN_DURATION = 10; // secs
const MAX_DURATION = 60; // secs
const ACCEPTED_MIME = [
  'video/mp4',
  'video/quicktime', // MOV
  'video/mpeg', // MPEG
  'video/x-msvideo' // AVI
];

type VideoUploadModalProps = {
  isEditing: boolean;
  value?: string;
  commitEdit: ( url: string ) => void;
  cancelEdit: () => void;
  handleDrop: ( files: FileWithPath[]) => void;
};


const VideoUploadModal = ({ isEditing, value = '', handleDrop, commitEdit, cancelEdit }: VideoUploadModalProps ) => {
  const { t } = useTranslation( 'grid' );

  // local state
  const [ urlInput, setUrlInput ] = useState( value );
  const [ filePreview, setFilePreview ] = useState<string | null>( null );
  const [ error, setError ] = useState<string | null>( null );
  const chosenUrl = filePreview ?? urlInput.trim();

  //reset
  useEffect(() => {
    if ( isEditing ) {
      setUrlInput( value );
      setFilePreview( null );
      setError( null );
    }
  }, [ isEditing, value ]);

  //validation
  const validateFile = useCallback(( file: File ): Promise<string | null> =>
    new Promise(( resolve ) => {
      if ( file.size > MAX_SIZE ) {
        return resolve(
          t(
            'video_error_size',
            'File size exceeds 100 MB. Please upload a smaller video.'
          )
        );
      }
      if ( !ACCEPTED_MIME.includes( file.type )) {
        return resolve(
          t(
            'video_error_format',
            'Unsupported format. Allowed: MP4, MOV, MPEG, AVI.'
          )
        );
      }
      const url = URL.createObjectURL( file );
      const v = document.createElement( 'video' );
      v.preload = 'metadata';
      v.src = url;

      v.onloadedmetadata = () => {
        URL.revokeObjectURL( url );
        const { videoWidth: w, videoHeight: h, duration } = v;
        const vertRatio = h / w;

        if ( vertRatio < 1 ) {
          return resolve(
            t(
              'video_error_orientation',
              'Video must be vertical (9 : 16 orientation).'
            )
          );
        }
        if ( h < MIN_HEIGHT || w < MIN_WIDTH ) {
          return resolve(
            t(
              'video_error_resolution',
              `Minimum resolution is ${MIN_WIDTH}×${MIN_HEIGHT}px.`
            )
          );
        }

        if ( duration < MIN_DURATION || duration > MAX_DURATION ) {
          return resolve(
            t(
              'video_error_duration',
              'Duration must be between 10 s and 60 s.'
            )
          );
        }

        resolve( null ); 
      };

      v.onerror = () =>
        resolve(
          t(
            'video_error_generic',
            'Could not read video metadata. Please try another file.'
          )
        );
    }), [t]);

  //drop
  const onDrop = useCallback(
    async ( files: FileWithPath[]) => {
      setError( null );
      if ( !files.length ) return;

      const file = files[0];
      const validationMsg = await validateFile( file );

      if ( validationMsg ) {
        setError( validationMsg );
        setFilePreview( null );
        return;
      }

      const objectUrl = URL.createObjectURL( file );
      setFilePreview( objectUrl );
      setUrlInput( '' ); 
      handleDrop( files ); 
    },
    [ handleDrop, validateFile ]
  );

  // for preview
  const isYouTube = !filePreview && ( urlInput.includes( 'youtube.com' ) || urlInput.includes( 'youtu.be' ));

  const youtubeEmbedUrl = (() => {
    if ( !isYouTube ) return '';
    let id = '';
    if ( urlInput.includes( 'youtube.com/watch?v=' )) {
      id = urlInput.split( 'v=' )[1]?.split( '&' )[0] ?? '';
    } else if ( urlInput.includes( 'youtu.be/' )) {
      id = urlInput.split( 'youtu.be/' )[1]?.split( '?' )[0] ?? '';
    }
    return id ? `https://www.youtube.com/embed/${id}` : '';
  })();

 
  return (
    <Modal
      size="lg"
      opened={isEditing}
      onClose={cancelEdit}
      title={t( 'upload_video_modal_title', 'Upload Video' )}
      trapFocus={false}
    >
      <Stack gap="md">
        <TextInput
          label={t( 'video_url_label', 'Video URL' )}
          placeholder="https://www.youtube.com/watch?v=… | https://example.com/video.mp4"
          value={urlInput}
          onChange={( e ) => {
            setUrlInput( e.currentTarget.value );
            setFilePreview( null );
            setError( null );
          }}
        />

        <Dropzone
           onDrop={onDrop}
           accept={ACCEPTED_MIME}
           maxSize={MAX_SIZE}
           multiple={false}
           style={{ marginBottom: '0.5rem' }}
           onReject={( files ) => {
              setError(
                t(
                  'video_error_format',
                  'Unsupported file. Allowed: MP4, MOV, MPEG, AVI up to 100 MB.'
                )
              );
          }}
        >
          <Group justify="center" gap="md" style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload
                size={42}
                color="var(--mantine-color-blue-6)"
                stroke={1.5}
              />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX
                size={42}
                color="var(--mantine-color-red-6)"
                stroke={1.5}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconVideo
                size={42}
                color="var(--mantine-color-dimmed)"
                stroke={1.5}
              />
            </Dropzone.Idle>

            <div>
              <Text size="sm" inline>
                {t(
                  'dropzone_video_title',
                  'Drag a video here or click to select'
                )}
              </Text>
              <Text size="xs" c="dimmed" inline mt={7}>
                {t(
                  'dropzone_video_info',
                  'Allowed: MP4, MOV, MPEG, AVI • 360×640 vertical • 10-60 s • ≤100 MB'
                )}
              </Text>
            </div>
          </Group>
        </Dropzone>

        {error && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="red"
            title={t( 'video_invalid', 'Invalid video' )}
          >
            {error}
          </Alert>
        )}

        {/* preview */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '0.5rem'
          }}
        >
          {isYouTube && youtubeEmbedUrl ? (
            <iframe
              width="100%"
              height="315"
              src={youtubeEmbedUrl}
              title="YouTube player"
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : chosenUrl ? (
            <video
              controls
              width="100%"
              style={{ maxHeight: '315px' }}
              src={chosenUrl}
              autoPlay
            >
              {t( 'video_not_supported', 'Your browser does not support video.' )}
            </video>
          ) : (
            <div
              style={{
                width: '100%',
                height: '200px',
                backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text size="sm" c="dimmed">
                {t( 'no_video_selected', 'No video selected' )}
              </Text>
            </div>
          )}
        </div>

        <Button
          fullWidth
          disabled={!chosenUrl || Boolean( error )}
          onClick={() => commitEdit( chosenUrl )}
        >
          {t( 'upload_video_save_button', 'Save' )}
        </Button>
      </Stack>
    </Modal>
  );
};

export default VideoUploadModal;
